import { createStart, createMiddleware } from "@tanstack/react-start";

import { renderErrorPage } from "./lib/error-page";
import { attachSupabaseAuth } from "@/integrations/supabase/auth-attacher";

const noindexMiddleware = createMiddleware().server(async ({ next, request }) => {
  const url = new URL(request.url);
  const result = await next();
  if (["/login", "/signup", "/forgot-password"].includes(url.pathname)) {
    result.response.headers.set("X-Robots-Tag", "noindex, nofollow");
  }
  return result;
});

/**
 * TTFB: the marketing pages render identical HTML for every visitor, so let the
 * CDN serve them. `max-age=0` keeps the browser revalidating (visitors always
 * see fresh copy after a publish) while `s-maxage` + `stale-while-revalidate`
 * mean the edge answers almost every request without waking SSR.
 *
 * Auth and app routes are per-visitor — they stay explicitly uncacheable so a
 * shared cache can never hand one user another user's HTML.
 */
const CACHEABLE_PUBLIC_PATHS = new Set([
  "/",
  "/about",
  "/pricing",
  "/contact",
  "/incident-reporting",
  "/lead-magnet",
]);


const cacheHeaderMiddleware = createMiddleware().server(async ({ next, request }) => {
  const url = new URL(request.url);
  const result = await next();
  if (request.method !== "GET") return result;
  const headers = result.response.headers;
  // Never override a handler that set its own caching policy (API/webhooks).
  if (headers.has("cache-control")) return result;
  const isHtml = (headers.get("content-type") ?? "").includes("text/html");
  if (!isHtml) return result;

  if (CACHEABLE_PUBLIC_PATHS.has(url.pathname)) {
    headers.set(
      "cache-control",
      "public, max-age=0, must-revalidate, s-maxage=300, stale-while-revalidate=86400",
    );
  } else {
    headers.set("cache-control", "private, no-store");
  }
  return result;
});


const errorMiddleware = createMiddleware().server(async ({ next, request }) => {
  const url = new URL(request.url);
  if (url.pathname.startsWith("/lovable/")) {
    return next();
  }
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }
    console.error(error);
    return new Response(renderErrorPage(), {
      status: 500,
      headers: { "content-type": "text/html; charset=utf-8" },
    });
  }
});

export const startInstance = createStart(() => ({
  requestMiddleware: [noindexMiddleware, cacheHeaderMiddleware, errorMiddleware],
  functionMiddleware: [attachSupabaseAuth],
}));
